import { Field, FieldRenderProps } from '@progress/kendo-react-form';
import { NumericTextBox, NumericTextBoxProps } from '@progress/kendo-react-inputs';
import {
    FieldInput,
    FileUpload,
    DialogProps,
    DisputeDialog,
    DisputeEventResult,
    DisputeField,
    FieldWrapper,
    ResolutionReasonComboBox,
    resolutionReasonValidator,
} from 'components';
import { DisputeEventTypes } from 'enums';
import { useDisputeService } from 'services';
import { useDisputeDetails, useUserInfo } from 'state';
import { useFileUploadValidator } from 'hooks';
import styles from './ApproveDispute.module.scss';

/**
 * ApproveDispute component allows the user to close or approve a dispute.
 * 
 * @param props - Additional properties passed to the Dialog
 * @returns Rendered component
 */
const ApproveDispute = (props: DialogProps) => {
    const { disputeAmount } = useDisputeDetails();
    const disputeService = useDisputeService();
    const filesValidator = useFileUploadValidator(0);
    const { isFinalApprover } = useUserInfo();

    const onSubmit = async (data: any) => {
        const { disputeNumber, files, reason, ...rest } = data;
        const fileData = files?.map((file: any) => file.getRawFile()) ?? [];
        const result = await disputeService?.resolveDispute(disputeNumber, {
            ...rest,
            eventTypeId: DisputeEventTypes.DisputeApproved,
            resolutionReasonId: reason.resolutionReasonId
        }, fileData);
        return {
            error: !result && 'Error saving dispute'
        } as DisputeEventResult;
    };

    return (
        <DisputeDialog
            initialValues={{ approvedAmount: disputeAmount, isPublic: false }}
            title="Approve Dispute"
            submitLabel="Approve"
            routeToNext={!isFinalApprover}
            onSubmit={onSubmit}
            {...props}
        >
            <DisputeField
                name="reason"
                className={styles.input}
                component={ResolutionReasonComboBox}
                validator={resolutionReasonValidator}
                type={DisputeEventTypes.DisputeApproved}
            />
            <DisputeField
                name="approvedAmount"
                component={ApprovedAmount}
                validator={amountValidator}
                max={disputeAmount}
            />
            <Field
                component={FieldInput}
                child={FileUpload}
                label="Please provide any additional documentation."
                name="files"
                validator={filesValidator}
            />            
        </DisputeDialog>
    );
};

const ApprovedAmount = (props: FieldRenderProps) => (
    <FieldWrapper label="Approved Amount" required {...props}>
        <NumericTextBox
            id="approvedAmount"
            aria-required="true"
            ariaLabelledBy="approvedAmount_label"
            className={styles.input}
            min={0.01}
            format="c2"
            spinners={false}
            {...props as NumericTextBoxProps}
        />
    </FieldWrapper>
);

const amountValidator = (value: number) => value > 0 ? '' : 'Required. Please enter the amount approved';

export default ApproveDispute;